import { Injectable, inject } from "@angular/core";
import { Router, RouterStateSnapshot, ActivatedRouteSnapshot, CanActivateFn } from "@angular/router";
import { EncryptDecryptService } from "../encrypt-decrypt.service";
import { RolesService } from "../roles.service";
import { StorageService } from "../storage.service";
 
@Injectable()
export class AuthGuard {
  constructor(
    private router: Router,
    private storageService: StorageService,
    private encryptDecryptService: EncryptDecryptService,
    private rolesService: RolesService
  ) {}

  private toBooleanFlag(value: any): boolean {
    if (value === true || value === 1) {
      return true;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'y' || normalized === 'yes';
    }
    return false;
  }
 
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Promise<boolean> {
    if (!this.encryptDecryptService.hasPassPhrase()) {
      let country = this.storageService.getWithDefaultPassphrase("Country");
      let groupCode = this.storageService.getWithDefaultPassphrase("groupCode") !== 'undefined' ? this.storageService.getWithDefaultPassphrase("groupCode") : undefined;
      if (country !== "KSA") {
        return new Promise((res) => {
          this.rolesService.getPassPhrase(country, groupCode).subscribe({
            next: (resp: any) => {
              let decryptedResponse = this.encryptDecryptService.decryptWithDefault(resp.response);

              let response = JSON.parse(decryptedResponse);
              if (response?.access == "CLIENT USER") {
                if (response?.data) {
                  this.encryptDecryptService.setPassPhrase(response.data);
                  let isAllowed = false;
                  if (!this.encryptDecryptService.hasPassPhrase()) return false;
                  if (this.storageService.tokenValidateTime()) isAllowed = true;

                  if (response?.id)
                    this.storageService.encodeValue("UserId", response.id);

                  if (response?.users) {
                    const result = response?.users?.[0]?.reduce(function (map, obj) {
                      map[obj.userId] = obj.userName;
                      return map;
                    }, {});

             sessionStorage.setItem("ApplicableUsers", JSON.stringify(result));

                  }

                  // Only write tin validator flag for MY country to avoid overwriting valid state
                  // when authenticating with non-MY countries.
                  if (country === "MY") {
                    const isTinValidatorEnabled = this.toBooleanFlag(response?.isMYTinValidatorEnabled);
                    this.storageService.encodeValue("isTinValidatorEnabled", String(isTinValidatorEnabled));
                  }

                  if (response?.groupCodeList) {
                    response?.groupCodeList?.sort((a, b) =>
                      a?.clientCode?.localeCompare(b.clientCode, undefined, { sensitivity: 'base' })
                    );
                    sessionStorage.setItem("groupCodeList", JSON.stringify(response?.groupCodeList));
                  }

                  if (response?.currentUser)
                    this.storageService.encodeValue("CurrentUser", response.currentUser);

                  if (response?.groupName)
                    this.storageService.encodeValue("Group", response.groupName);

                  if (response?.clientCode)
                    this.storageService.encodeValue("clientCode", response.clientCode);

                  this.storageService.encodeValue("policy", response.policy);

                  res(isAllowed);
                } else {
                  alert("You do not have access to the selected country.");
                  res(false);
                }
              } else {
                alert("You are not authorized to access the portal.");
                res(false);
              }
            },
            error: (error) => {
              if (error.status === 401 || error.status === 403) {
                this.storageService.logOut(false);
              } else if (error.status != 409) {
                alert("Something went wrong. Please try again later or reach out to helpdesk for assistance/support.");
              }
              res(false);
            }
          });
        });
      } else {
        return new Promise((res) => {
          this.rolesService.checkValidKSAUser().subscribe(
            (resp: any) => {
              if (!!resp.data) {
                this.encryptDecryptService.setPassPhrase(resp.data);
                let isAllowed = false;
                if (!this.encryptDecryptService.hasPassPhrase()) return false;
 
                if (
                  state.url.indexOf("/home/ksa/data-status/file-upload") > -1 ||
                  state.url.indexOf("/home/ksa/data-status/file-status") > -1 ||
                  state.url.indexOf("/home/ksa/data-status/api") > -1 ||
                  state.url.indexOf("/home/ksa/invoice-mgnt") > -1 ||
                  state.url.indexOf("/home/ksa/reports") > -1 ||
                  state.url.indexOf("/home/ksa") > -1 ||
                  state.url.indexOf("/home") > -1
                )
                  isAllowed = true;
 
                // if (this.storageService.tokenValidateTime())
                //     isAllowed = true;
 
                this.storageService.encodeValue("policy", "Y");
                res(isAllowed);
              } else {
                alert("You are not authorized to access the portal.");
                res(false);
              }
            },
            (error) => {
              if (error.status === 401 || error.status === 403) {
                this.storageService.logOut(false);
              } else if(error.status === 409) {
                console.log("Duplicate session found");
              } else
                alert("Something went wrong. Please try again later or reach out to helpdesk for assistance/support.");
              res(false);
            }
          );
        });
      }
    } else {
      let isAllowed = false;
      if (!this.encryptDecryptService.hasPassPhrase()) return false;
      if (
        state.url.indexOf("/home/dashboard") > -1 ||
        state.url.indexOf("/home/reports") > -1 ||
        state.url.indexOf("/home/data-status") > -1 ||
        state.url.indexOf("/home/invoice-management") > -1 ||
        state.url.indexOf("/home/ksa") > -1
      ) {
        isAllowed = true;
      }
 
      if (this.storageService.tokenValidateTime()) {
        isAllowed = true;
      }
      return isAllowed;
    }
  }
}
 
export const AuthGuardService: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return inject(AuthGuard).canActivate(route, state);
}